import React, { useCallback, useEffect, useRef, useState } from "react";
import { ReferenceArea } from "recharts";

/**
 * Drag-to-select-a-time-range across one or more recharts charts.
 *
 * Recharts hands back the hovered POINT INDEX, not a time, so the selection has
 * to be resolved against the same array the chart is rendering — that is why
 * `data` is passed in rather than the hook keeping its own copy.
 *
 * The window-level mouseup listener is not optional: releasing the button
 * outside the plot area never fires the chart's own onMouseUp, which would
 * otherwise leave the drag armed and the next hover painting a selection the
 * user never started.
 *
 * What happens on selection is the caller's business — the admin tab narrows a
 * client-side domain, the customer page refetches at a finer bucket.
 *
 * `enabled` exists for touch. A drag-select is a pointer gesture with no touch
 * equivalent that does not also mean "scroll the page": recharts synthesises
 * mouse events from taps, so on a phone every attempt to scroll past a chart
 * armed a selection and zoomed instead. Callers pass `!isMobile` and steer with
 * the calendar and range pickers there. Disabled means no handlers at all —
 * returning inert ones would still make recharts track the pointer.
 */
export function useChartZoom(
  data: Array<{ time: number }>,
  onSelect: (fromSec: number, toSec: number) => void,
  enabled: boolean = true
) {
  const [refLeft, setRefLeft] = useState<number | null>(null);
  const [refRight, setRefRight] = useState<number | null>(null);
  const dragRef = useRef<{ start: number | null; end: number | null }>({
    start: null,
    end: null,
  });

  const onMouseDown = useCallback((state: { activeTooltipIndex?: number }) => {
    if (state?.activeTooltipIndex == null) return;
    dragRef.current = { start: state.activeTooltipIndex, end: null };
    setRefLeft(state.activeTooltipIndex);
    setRefRight(null);
  }, []);

  const onMouseMove = useCallback((state: { activeTooltipIndex?: number }) => {
    if (state?.activeTooltipIndex == null || dragRef.current.start == null) return;
    dragRef.current.end = state.activeTooltipIndex;
    setRefRight(state.activeTooltipIndex);
  }, []);

  const onMouseUp = useCallback(() => {
    const start = dragRef.current.start;
    if (start == null) return;
    const end = dragRef.current.end ?? start;
    dragRef.current = { start: null, end: null };
    setRefLeft(null);
    setRefRight(null);
    if (data.length === 0) return;
    const t1 = data[Math.min(start, end)]?.time;
    const t2 = data[Math.max(start, end)]?.time;
    // A click is a drag of zero width; only a real span is a zoom.
    if (t1 != null && t2 != null && Math.abs(t2 - t1) > 1) {
      onSelect(Math.min(t1, t2), Math.max(t1, t2));
    }
  }, [data, onSelect]);

  useEffect(() => {
    if (!enabled) return;
    const onWindowMouseUp = () => {
      if (dragRef.current.start != null) onMouseUp();
    };
    window.addEventListener("mouseup", onWindowMouseUp);
    return () => window.removeEventListener("mouseup", onWindowMouseUp);
  }, [enabled, onMouseUp]);

  // A drag left half-finished when the viewport crosses the breakpoint would
  // otherwise keep painting a band no gesture can now clear.
  useEffect(() => {
    if (enabled) return;
    dragRef.current = { start: null, end: null };
    setRefLeft(null);
    setRefRight(null);
  }, [enabled]);

  /** The blue band under the cursor. Render as a child of every chart. */
  const selection = () =>
    enabled && refLeft != null && refRight != null && data.length > 0 ? (
      <ReferenceArea
        x1={data[Math.min(refLeft, refRight)]?.time}
        x2={data[Math.max(refLeft, refRight)]?.time}
        strokeOpacity={0.4}
        stroke="#5794f2"
        fill="#5794f2"
        fillOpacity={0.3}
      />
    ) : null;

  return {
    handlers: enabled ? { onMouseDown, onMouseMove, onMouseUp } : {},
    selection,
  };
}
